import React, { Component } from 'react';
import ldCloneDeep from 'lodash/cloneDeep';
import { RouteComponentProps } from 'react-router-dom';
import CountTable, { ReportTableRow, tabContent } from '../../molecules/CountTable';
import { Route, Switch } from 'react-router-dom';
import ReportHeader from '@common/component/organisms/ReportHeader';
import { ContentHeaderProps } from '@common/component/organisms/ContentHeader';
import { SearchConditionState } from '@common/redux/reducers/SearchConditionReducer';
import {
  commodityCategoryContractTypeMapping,
  isSameSearchCondition,
  LINE_TYPE,
  negotiationResultTypeMapping,
  negotiationStatusTypeMapping,
} from '@common/Common';
import { filterConditionState, filterKeyState } from '@common/redux/reducers/CurrentFilterReducer';
import Layout from '../Layout';
import FilterSelectorContainer from '../../../container/molecules/FilterSelectorContainer';
import CircularProgress from '@common/component/atoms/CircularProgress';
import { CommodityCategoryContractInfo, SearchBranch } from '@common/external/AggregationApi';
import { searchBranchStatusState } from '@common/redux/reducers/SearchBranchStatusReducer';
import Drawer from '@common/component/atoms/Drawer';
import IconButton from '@common/component/atoms/IconButton';
import Button from '@common/component/atoms/Button';
import SearchConditionInput from '@common/container/molecules/SearchConditionInputContainer';
import { Badge } from '@common/component/atoms/Badge';
import { Collapse, Icon } from '@material-ui/core';
import classNames from 'classnames';
import { StoreAndAgencyInfo, StoreSelectorState } from '@common/redux/reducers/StoreSelectorReducer';
import StoreSelectModal from '../../../container/organisms/StoreSelectModalContainer';
import moment from 'moment';
import { getAgencyStoreShortName } from '@common/component/organisms/FilterSettingDrawer/function';
import { AnnotationTooltipProps } from '@common/component/molecules/AnnotationTooltip';

export interface NegotiationHandler {
  userId: string;
  isLoggedIn: boolean;
  agencyCd: string[];
  reportTableRow: ReportTableRow;
  searchBranchNextAction: { [key: string]: SearchBranch };
  searchBranchPending: { [key: string]: SearchBranch };
  searchBranchPeriod: { [key: string]: SearchBranch };
  searchBranchProb: { [key: string]: SearchBranch };
  searchBranchResult: { [key: string]: SearchBranch };
  searchBranchStatus: searchBranchStatusState;
  searchBranchCommodityCategoryContract: CommodityCategoryContractInfo;
  currentFilterCondition: filterKeyState;
  filterList: filterConditionState[];
  header: ContentHeaderProps;
  tabContent: tabContent[];
  tooltipInfo: AnnotationTooltipProps;
  storeSelector: StoreSelectorState;
  updateSearchCondition(searchCondition: SearchConditionState): void;
  aggregations: (props: aggregationsProps) => void;
  updateRefresh: (props: aggregationsProps) => void;
  clearFilter: () => void;
  setToggleModal: () => void;
  setToggleStore: (store: StoreAndAgencyInfo) => void;
}

export interface AggregationsState {
  isOpenDrawer: boolean;
  isCollapsedStore: boolean;
}

export interface aggregationsProps {
  userId: string;
  agencyCd: string[];
  filterKey: string;
}

type extendProps = NegotiationHandler & RouteComponentProps<{ type: string }>;

class Negotiation extends Component<extendProps, AggregationsState> {
  constructor(props: extendProps) {
    super(props);
    this.state = {
      isOpenDrawer: false,
      isCollapsedStore: false,
    };
    this.updateSearchCondition = this.updateSearchCondition.bind(this);
  }

  componentDidMount() {
    //件数取得API
    const props: aggregationsProps = {
      userId: this.props.userId,
      agencyCd: this.props.agencyCd,
      filterKey: this.props.currentFilterCondition.filterKey,
    };
    if (this.props.isLoggedIn) {
      this.props.aggregations(props);
    }
    // Refresh buttonの更新
    this.props.updateRefresh(props);
  }

  componentWillUpdate(nextProps: extendProps) {
    const props: aggregationsProps = {
      userId: nextProps.userId,
      agencyCd: nextProps.agencyCd,
      filterKey: nextProps.currentFilterCondition.filterKey,
    };

    if (nextProps.isLoggedIn && !this.props.isLoggedIn) {
      this.props.aggregations(props);
    }
    if (
      nextProps.agencyCd !== this.props.agencyCd ||
      nextProps.currentFilterCondition.filterKey !== this.props.currentFilterCondition.filterKey
    ) {
      // Refresh buttonの更新
      this.props.updateRefresh(props);

      if (this.props.isLoggedIn) {
        this.props.aggregations(props);
      }
    }
  }

  updateSearchCondition(lineType: string, rowKey: string, colKey: string) {
    // Step1.
    let searchConditions: SearchConditionState;

    // Step2. フィルター検索条件適用
    let filterCondition: SearchConditionState = [];

    if (this.props.currentFilterCondition.filterKey) {
      filterCondition = this.props.filterList.filter((val) => val.filterKey === this.props.currentFilterCondition.filterKey)[0]
        .searchConditions;
    }
    searchConditions = [...filterCondition];

    // Step3. 画面固有の条件
    const searchBranchStatusCopy: SearchBranch[] = [...this.props.searchBranchStatus.result];
    searchBranchStatusCopy.forEach((searchBranch) => {
      if (searchBranch.branchConditionName === 'negotiationStatusType') {
        const negotiationStatusTypeSearchBranch = ldCloneDeep(negotiationStatusTypeMapping[searchBranch.specifiedCondition[0]]);
        negotiationStatusTypeSearchBranch.forEach((negotiationStatusType) => {
          searchConditions.push({
            searchConditionName: negotiationStatusType.branchConditionName,
            specifiedCondition: negotiationStatusType.specifiedCondition,
          });
        });
      } else {
        searchConditions.push({
          searchConditionName: searchBranch.branchConditionName,
          specifiedCondition: searchBranch.specifiedCondition,
        });
      }
    });

    // Step4. クリックされた行による条件
    if (rowKey !== 'total' && rowKey !== 'customers') {
      const commodityCategoryContractType = this.props.searchBranchCommodityCategoryContract[rowKey];
      commodityCategoryContractTypeMapping[commodityCategoryContractType].forEach((searchBranch) => {
        searchConditions.push({
          searchConditionName: searchBranch.branchConditionName,
          specifiedCondition: searchBranch.specifiedCondition,
        });
      });
    }

    // Step5. クリックされた列による条件
    if (colKey !== 'total') {
      let searchBranch: { [key: string]: SearchBranch } = {};
      switch (lineType) {
        case LINE_TYPE.PERIOD:
          searchBranch = Object.assign({}, this.props.searchBranchPeriod);
          break;
        case LINE_TYPE.ACTION:
          searchBranch = Object.assign({}, this.props.searchBranchNextAction);
          break;
        case LINE_TYPE.PROB:
          searchBranch = Object.assign({}, this.props.searchBranchProb);
          break;
        case LINE_TYPE.PENDING:
          searchBranch = Object.assign({}, this.props.searchBranchPending);
          break;
        case LINE_TYPE.RESULT:
          searchBranch = Object.assign({}, this.props.searchBranchResult);
          break;
      }

      const colSearchConditions: SearchConditionState = [...searchConditions];
      // Step5-1. 商談終了の場合、変換処理必要
      if (searchBranch[colKey].branchConditionName === LINE_TYPE.RESULT) {
        const searchBranchList = negotiationResultTypeMapping[searchBranch[colKey].specifiedCondition[0]];
        if (searchBranchList.length > 0) {
          // Step1. 指定条件配列生成
          const convertNegotiationResultCondition: string[] = [];
          for (let i = 0; i < searchBranchList.length; i++) {
            for (let j = 0; j < searchBranchList[i].specifiedCondition.length; j++) {
              convertNegotiationResultCondition.push(searchBranchList[i].specifiedCondition[j]);
            }
          }
          // Step2. 検索条件更新
          const findIndex = searchConditions.findIndex((element) => element.searchConditionName === 'negotiationProgressType');
          if (findIndex !== -1) {
            colSearchConditions[findIndex] = { ...colSearchConditions[findIndex], specifiedCondition: convertNegotiationResultCondition };
          }
        }
      } else {
        const findIndex = searchConditions.findIndex((element) => element.searchConditionName === searchBranch[colKey].branchConditionName);
        if (findIndex !== -1) {
          colSearchConditions[findIndex] = {
            ...colSearchConditions[findIndex],
            specifiedCondition: searchBranch[colKey].specifiedCondition,
          };
        } else {
          colSearchConditions.push({
            searchConditionName: searchBranch[colKey].branchConditionName,
            specifiedCondition: searchBranch[colKey].specifiedCondition,
          });
        }
      }
      searchConditions = colSearchConditions;
    }

    // Step6. 特定仕様
    // 共通検索条件[undispFlg:0]を追加
    searchConditions.push({
      searchConditionName: 'undispFlg',
      specifiedCondition: ['0'],
    });
    switch (lineType) {
      case LINE_TYPE.PERIOD:
        // [対応期間別]：未入力をクリックする場合、検索条件を追加
        if (colKey === 'unset') {
          searchConditions.push({
            searchConditionName: 'actionInfo',
            specifiedCondition: [null],
          });
        }
        break;
      case LINE_TYPE.ACTION: {
        // レポート[次のアクション]の未入力をクリックする場合、検索条件を追加
        if (colKey === '00') {
          // Step1. 検索条件[actionInfo]を追加
          searchConditions.push({
            searchConditionName: 'actionInfo',
            specifiedCondition: [null],
          });
          // Step2. 検索条件[actionDate]を削除
          const actionDateIndex = searchConditions.findIndex((element) => element.searchConditionName === 'actionDate');
          if (actionDateIndex !== -1) {
            searchConditions.splice(actionDateIndex, 1);
          }
        }
        // レポート[次のアクション]の合計をクリックする場合、検索条件を追加
        if (colKey === 'total') {
          // 商談中の場合、actionInfo :[null] 検索条件を追加
          const actionDateIndex = searchConditions.findIndex((element) => element.searchConditionName === 'actionDate');
          if (searchConditions[actionDateIndex].specifiedCondition[0] === null) {
            searchConditions.push({
              searchConditionName: 'actionInfo',
              specifiedCondition: [null],
            });
          }
        }
        break;
      }
      case LINE_TYPE.PROB: {
        // 商談中の場合、actionInfo :[null] 検索条件を追加
        const actionDateIndex = searchConditions.findIndex((element) => element.searchConditionName === 'actionDate');
        if (searchConditions[actionDateIndex].specifiedCondition[0] === null) {
          searchConditions.push({
            searchConditionName: 'actionInfo',
            specifiedCondition: [null],
          });
        }
        break;
      }
      case LINE_TYPE.PENDING: {
        //復活予定
        if (colKey === 'restoration') {
          // Step1. 検索条件[undispFlg]を追加
          const undispFlgIndex = searchConditions.findIndex((element) => element.searchConditionName === 'undispFlg');
          if (undispFlgIndex !== -1) {
            searchConditions[undispFlgIndex] = {
              searchConditionName: 'undispFlg',
              specifiedCondition: ['1'],
            };
          }
          // Step2. 検索条件[actionDate]を削除
          const actionDateIndex = searchConditions.findIndex((element) => element.searchConditionName === 'actionDate');
          if (actionDateIndex !== -1) {
            searchConditions.splice(actionDateIndex, 1);
          }
          // Step3. 検索条件[negotiationProgressType]を削除
          const negotiationProgressTypeIndex = searchConditions.findIndex(
            (element) => element.searchConditionName === 'negotiationProgressType',
          );
          if (negotiationProgressTypeIndex !== -1) {
            searchConditions.splice(negotiationProgressTypeIndex, 1);
          }
        }
        //合計
        if (colKey === 'total') {
          // Step1. 検索条件[resurrectionFlg]を追加
          searchConditions.push({
            searchConditionGroupId: 1,
            searchConditionName: 'resurrectionFlg',
            specifiedCondition: ['1'],
          });
          // Step2. 検索条件[undispFlg]を追加
          searchConditions.push({
            searchConditionGroupId: 1,
            searchConditionName: 'undispFlg',
            specifiedCondition: ['1'],
          });
          // Step3. 検索条件[actionDate]の終了日を修正
          const actionDateIndex = searchConditions.findIndex((element) => element.searchConditionName === 'actionDate');
          if (actionDateIndex !== -1) {
            searchConditions[actionDateIndex].specifiedCondition[1] = moment(searchConditions[actionDateIndex].specifiedCondition[0])
              .add(3, 'months')
              .startOf('month')
              .subtract(1, 'days')
              .format('YYYYMMDD');
          }
          // Step4. 検索条件[commodityCategoryType]を追加
          const commodityCategoryTypeIndex = searchConditions.findIndex(
            (element) => element.searchConditionName === 'commodityCategoryType',
          );
          if (commodityCategoryTypeIndex !== -1) {
            searchConditions.push({
              searchConditionGroupId: 1,
              searchConditionName: 'commodityCategoryType',
              specifiedCondition: searchConditions[commodityCategoryTypeIndex].specifiedCondition,
            });
          }
          // Step5. 検索条件[mobileContractType]を追加
          const mobileContractTypeIndex = searchConditions.findIndex((element) => element.searchConditionName === 'mobileContractType');
          if (mobileContractTypeIndex !== -1) {
            searchConditions.push({
              searchConditionGroupId: 1,
              searchConditionName: 'mobileContractType',
              specifiedCondition: searchConditions[mobileContractTypeIndex].specifiedCondition,
            });
          }
        }
        break;
      }
      default:
        break;
    }

    //反映
    if (this.props.currentFilterCondition.filterKey && !isSameSearchCondition(searchConditions, filterCondition)) {
      this.props.clearFilter();
    }
    this.props.updateSearchCondition(searchConditions);
  }

  // 絞り込みドロワー
  renderDrawer() {
    return (
      <Drawer
        className='filter-drawer'
        isOpen={this.state.isOpenDrawer}
        isOverayTransparent
        onClose={() => this.setState({ isOpenDrawer: false })}
        position='right'>
        <div className='filter-drawer__inner' data-testid='filterSettingDrawer'>
          <div className='filter-drawer__header'>
            <h3 className='headline-4 u-float-left'>絞り込み条件</h3>
            <IconButton
              data-testid='drawer-close-button'
              className='u-float-right u-mn8'
              icon='close'
              onClick={() => this.setState({ isOpenDrawer: false })}
            />
          </div>
          <div className='filter-drawer__content u-p0'>
            <div className='u-pv24 u-ph16'>
              <FilterSelectorContainer
                updateNotice={() => {
                  /**/
                }}
              />
              <>
                <hr className='uc-divider u-mb8 u-mhn16' />
                <div className='uc-form-field u-mt24'>
                  <div className='uc-form-field__label　u-float-left u-mb16'>
                    <span
                      className='filter-drawer__collapse'
                      onClick={() => this.setState({ isCollapsedStore: !this.state.isCollapsedStore })}>
                      <Icon className={classNames('filter-drawer__arrow-icon', this.state.isCollapsedStore && 'is-rotate')}>
                        keyboard_arrow_up
                      </Icon>
                      <span className='u-text-middle u-text-bold'>表示する店舗</span>
                    </span>
                    {/* クリックして店舗選択モーダルを表示する */}
                    <Button
                      className='u-w5s u-float-right'
                      type='transparent'
                      height='s'
                      leftIcon='add'
                      label='店舗を追加'
                      onClick={() => this.props.setToggleModal()}
                    />
                  </div>
                  <Collapse in={!this.state.isCollapsedStore}>
                    <div className='uc-form-field__control'>
                      {this.props.storeSelector.sortedModalSelectedStores
                        .map((agencyCd) => this.props.storeSelector?.stores?.[agencyCd] || this.props.storeSelector?.agencies?.[agencyCd])
                        .map(
                          (store) =>
                            store && (
                              <Badge
                                className='agency-store-tag u-mr8 u-mb8'
                                activeClassName='agency-store-tag--active'
                                isActive={store?.selectedForFilter ?? false}
                                onClick={() => {
                                  this.props.setToggleStore(store);
                                }}>
                                {getAgencyStoreShortName(store.shortName, store.agencyCd, String(store.companyCfc))}
                              </Badge>
                            ),
                        )}
                      {['dev'].includes(process.env.REACT_APP_ENV ?? '') && (
                        <>
                          {/* スタイリング見せるため */}
                          {['ソフトバンク舞浜', 'ソフトバンク桜木町', 'ソフトバンク赤坂'].map((item, index) => (
                            <Badge className='agency-store-tag u-mr8 u-mb8' activeClassName='agency-store-tag--active' isActive={index > 3}>
                              {item}
                            </Badge>
                          ))}
                          <p className='u-text-m u-text-disabled'>設定されていません</p>;
                        </>
                      )}
                    </div>
                  </Collapse>
                  <StoreSelectModal />
                </div>
              </>
            </div>
          </div>
          <div className='filter-drawer__footer'>
            <Button
              onClick={() => this.setState({ isOpenDrawer: false })}
              className='u-mr16'
              type='transparent'
              label='キャンセル'
              data-testid-e2e='filterSettingDrawer-close'
              data-testid='filterSettingDrawer-close'
            />
          </div>
        </div>
      </Drawer>
    );
  }

  render() {
    const { type } = this.props.match.params;

    return (
      <Layout isLoggedIn={this.props.isLoggedIn}>
        <div className='layout-tab'>
          <ReportHeader
            header={this.props.header}
            activeTabKey={type}
            tabList={this.props.tabContent.map((row) => row.tabkey)}
            tooltipInfo={this.props.tooltipInfo}
            onTabClick={() => {
              /**/
            }}
            data-testid='reportHeader'>
            <SearchConditionInput
              toggleDrower={() => {
                this.setState({ isOpenDrawer: !this.state.isOpenDrawer });
              }}
              data-testid='searchConditionInput'
            />
            {this.renderDrawer()}
          </ReportHeader>
          <div className='layout-tab__content'>
            {this.props.header.loadingStatus.isLoading ? (
              <div className='u-flex u-h-viewport u-items-center u-justify-center' data-testid='loadingArea'>
                <div className='u-text-center'>
                  <CircularProgress isIndeterminate hasBackground={false} />
                  <p>ページを読み込んでいます...</p>
                </div>
              </div>
            ) : (
              <Switch>
                {this.props.tabContent.map((tabContent) => (
                  <Route
                    key={tabContent.tabkey.type}
                    path={tabContent.tabkey.url}
                    component={() => (
                      <CountTable
                        tabContent={tabContent}
                        reportTableRow={this.props.reportTableRow}
                        updateSearchCondition={this.updateSearchCondition}
                        headerStatus={this.props.header.statusType}
                        isLoading={this.props.header.loadingStatus.isLoading}
                      />
                    )}
                    data-testid='route'
                  />
                ))}
              </Switch>
            )}
          </div>
        </div>
      </Layout>
    );
  }
}

export default Negotiation;
